// src/pages/DigidateDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Helper: haal ID + (optionele) privacy hash uit een Vimeo URL
function getVimeoIdAndHash(url) {
  if (!url) return {};
  const match = /vimeo\.com\/(\d+)(?:\/([a-z0-9]+))?/i.exec(url);
  return match ? { id: match[1], hash: match[2] || null } : {};
}

function DigidateDetail() {
  const { slug } = useParams();
  const [digidate, setDigidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    const fetchDigidate = async () => {
      setLoading(true);
      setError('');
      if (!jwt) {
        setError("Authenticatie vereist.");
        setLoading(false);
        return;
      }

      // Let op: Blocks velden (description, registration_intro) komen standaard mee.
      // We populaten alleen relaties/media die we nodig hebben.
      const populateFields = [
        'image',
        'experts.photo',
        'related_doorloopjes.steps.image',
        'downloads'
      ];
      
      const populateQuery = populateFields.map(field => `populate=${field}`).join('&');

      try {
        const url = `${STRAPI_URL}/api/digidates?filters[slug][$eq]=${slug}&${populateQuery}`;
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${jwt}` } });

        if (response.data.data.length > 0) {
          setDigidate(response.data.data[0]);
        } else {
          setError("Webinar niet gevonden.");
        }
      } catch (err) {
        setError("Dit webinar laden lukte niet. Probeer het opnieuw.");
        console.error("Fout bij ophalen Webinar detail:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDigidate();
  }, [slug]);

  if (loading) return <p>Details worden geladen...</p>;
  if (error || !digidate) return <p>{error || 'Dit webinar bestaat niet of is verplaatst.'}</p>;

  const registrationUrl = digidate.registration_url;

  return (
    <div className="detail-page-container article-view">
      <main className="detail-main-content">
        <p className="detail-breadcrumb">
          Webinar op {new Date(digidate.event_date).toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <h1
          className="detail-title"
          style={{
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '2.2rem',
          }}
        >
          {digidate.title}
        </h1>
        
        {digidate.has_taken_place ? (
          digidate.recording_url ? (
            (() => {
              const { id: vimeoId, hash: vimeoHash } = getVimeoIdAndHash(digidate.recording_url);
              if (vimeoId) {
                return (
                  <div className="video-player-wrapper" style={{ marginTop: '1.5rem', marginBottom: '2rem', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      src={`https://player.vimeo.com/video/${vimeoId}${
                        vimeoHash ? `?h=${vimeoHash}&` : '?'
                      }color=ffdd00&title=0&byline=0&portrait=0`}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={digidate.title}
                    ></iframe>
                  </div>
                );
              }
              return (
                <a href={digidate.recording_url} target="_blank" rel="noopener noreferrer" className="btn-primary btn-recording">
                  Bekijk de opname
                </a>
              );
            })()
          ) : (
            <p className="info-message">De opname van dit webinar wordt binnenkort hier geplaatst.</p>
          )
        ) : (
          registrationUrl ? (
            <>
              {/* NIEUW: tekst BOVEN het inschrijf-iframe */}
              {digidate.registration_intro && (
                <div className="detail-description">
                  <BlocksRenderer content={digidate.registration_intro} />
                </div>
              )}

              <div className="iframe-container">
                <iframe
                  src={registrationUrl}
                  width="100%"
                  height="340"   // hoogte 60% kleiner gemaakt
                  title={`Inschrijfformulier voor ${digidate.title}`}
                ></iframe>
              </div>
            </>
          ) : (
            <p className="info-message">Het inschrijfformulier voor dit webinar is momenteel niet beschikbaar.</p>
          )
        )}

        {/* Bestaande tekst ONDER het iframe (blijft ongewijzigd) */}
        {digidate.description && (
          <div className="detail-description">
            <BlocksRenderer content={digidate.description} />
          </div>
        )}

        {digidate.experts && digidate.experts.length > 0 && (
          <>
            <h3 className="related-title">Sprekers</h3>
            <div className="experts-list">
              {digidate.experts.map(expert => (
                <div key={expert.id} className="expert-card">
                  {expert.photo?.url && <img src={expert.photo.url} alt={expert.name} />}
                  <h4>{expert.name}</h4>
                  <p>{expert.title}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {digidate.downloads && digidate.downloads.length > 0 && (
          <>
            <h3 className="related-title">Downloads</h3>
            <div className="downloads-list">
              {digidate.downloads.map(file => (
                <a key={file.id} href={file.url} target="_blank" rel="noopener noreferrer" className="download-link">
                  {file.name} <span>({(file.size / 1024).toFixed(1)} KB)</span>
                </a>
              ))}
            </div>
          </>
        )}
        
        {digidate.related_doorloopjes && digidate.related_doorloopjes.length > 0 && (
          <>
            <h3 className="related-title">Besproken Doorloopjes</h3>
            <div className="related-doorloopjes-list">
              {digidate.related_doorloopjes.map(d => (
                <RouterLink to={`/doorloopjes/${d.slug}`} key={d.id} className="doorloopje-card">
                  <div className="card-icon">
                    {d.steps?.[0]?.image?.formats?.thumbnail?.url && <img src={d.steps[0].image.formats.thumbnail.url} alt="" />}
                  </div>
                  <div className="card-text">
                    <h3>{d.title}</h3>
                  </div>
                  <div className="card-arrow">></div>
                </RouterLink>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default DigidateDetail;
